// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getAssetProjectHistory } from '@/asset/api/get-asset-project-history';
import { SparseAsset, sparseAssetSchema } from '@/asset/api/types';
import { AssetInventoryDateWarning } from '@/asset/components/asset-inventory-date-warning';
import { FormSparseAssetSearch } from '@/asset/components/form-sparse-asset-search';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormInputWarning } from '@/components/form-input-warning';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import { Project } from '@/project/api/types';
import { useAssetAssignment } from '@/project/hooks/use-asset-assignment';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

const assetSchema = sparseAssetSchema
  .nullable()
  .refine((asset): asset is SparseAsset => asset !== null, {
    message: 'Asset is required',
  });

const requiredDateSchema = dateSchema
  .nullable()
  .refine((date): date is DateTime => date !== null && date.isValid, {
    message: 'Effective date is required',
  })
  .refine((date) => date >= MINIMUM_DATE, {
    message: 'Effective date cannot be before January 1, 1970',
  });

interface InputData {
  date: DateTime | null;
  asset: SparseAsset | null;
}

const outputSchema = z
  .object({ asset: assetSchema, date: requiredDateSchema })
  .superRefine((data, context) => {
    const latestAllowedDate = DateTime.now().plus({ days: 1 }).endOf('day');

    if (data.date > latestAllowedDate) {
      context.addIssue({
        path: ['date'],
        code: z.ZodIssueCode.custom,
        message: 'Assets cannot be assigned in the future',
      });
    }
  });

type OutputData = z.infer<typeof outputSchema>;

interface Props {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
}

export function ProjectAssignmentDialog(props: Props) {
  const mutation = useAssetAssignment({
    onSuccess: () => {
      close();
      reset();
    },
  });

  const { control, reset, handleSubmit } = useForm<
    InputData,
    undefined,
    OutputData
  >({
    resolver: zodResolver(outputSchema),
    defaultValues: { asset: null, date: null },
  });

  const asset = useWatch({ control, name: 'asset' });
  const date = useWatch({ control, name: 'date' });

  const assignmentQuery = useQuery({
    enabled: asset !== null,
    queryKey: ['asset', asset?.key, 'project-history'],
    queryFn: () => {
      if (asset === null) {
        throw new Error('An asset is required to assign');
      }

      return getAssetProjectHistory(asset.key);
    },
  });

  let minimumDate: DateTime | undefined = undefined;

  if (assignmentQuery.data && assignmentQuery.data.history.length > 0) {
    const lastAssignment =
      assignmentQuery.data.history[assignmentQuery.data.history.length - 1];

    if (lastAssignment.end) {
      minimumDate = lastAssignment.end;
    }
  }

  const productLineItems = props.project.items.filter(
    (lineItem) => lineItem.item.hasAssets
  );

  const isWarningOpen =
    productLineItems.length > 0 &&
    productLineItems.every((lineItem) => {
      if (asset === null) {
        return false;
      }

      return lineItem.item.id !== asset.item.id;
    });

  const warningTooltip =
    asset === null
      ? null
      : `This project does not currently include the product ${asset?.item.name}, you may want to double check the asset`;

  const handleValidSubmission: SubmitHandler<OutputData> = (data) => {
    mutation.assign(props.project.key, [
      { id: data.asset.id, dateTime: data.date },
    ]);
  };

  const close = () => {
    reset({ asset: null, date: null });
    props.onClose();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={mutation.isError}
        message="An error occurred while assigning the asset."
        onClose={mutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Assign asset to {props.project.name}</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" alignItems="center" marginBottom={2}>
            <FormSparseAssetSearch
              name="asset"
              label="Asset"
              control={control}
              disabled={mutation.isPending}
              withProjectStatus={['none']}
              helperText="Select an unassigned asset"
            />
            <FormInputWarning isOpen={isWarningOpen} tooltip={warningTooltip} />
          </Stack>
          <Stack direction="row" alignItems="center">
            <FormDatePicker
              disableFuture
              name="date"
              label="Effective date"
              control={control}
              disabled={mutation.isPending}
              minimumDate={minimumDate}
            />
            <AssetInventoryDateWarning asset={asset} dateTime={date} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            disabled={mutation.isPending}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
